// eslint-disable-next-line no-redeclare
/* global fetch */

import { put, takeLatest } from "redux-saga/effects";

import {
	ResourcesLoadAction,
	ResourcesLoadFailedAction,
	ResourcesLoadSuccessAction
} from "../../actions/resources/resources";
import { SpriteDefResourceUri } from "generated/sprites";

/// / Resources Load
// Worker
function * loadResourcesWorker (action) {
	try {
		const fetchOpts = {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				"X-UPG-UI": "web"
			}
		};

		const r = yield fetch(SpriteDefResourceUri, fetchOpts)
			.then(async (response) => {
				if (response.status !== 200) {
					console.error("Failed to load resources", response.status);
					throw new Error("Failed to load resources");
				}

				// Some environments serve a compressed JSON blob (e.g., *.json.z)
				// without automatic decompression. Handle both compressed and plain JSON.
				const url = SpriteDefResourceUri;
				const encoding = (response.headers.get("content-encoding") || "").toLowerCase();
				const isCompressed = /gzip|deflate/.test(encoding) || /\.json\.z$/i.test(url);

				if (isCompressed) {
					// Read the body ONCE. We'll keep an untouched clone for fallback decoding.
					let buffer;
					try {
						buffer = await response.arrayBuffer();
					} catch (e) {
						console.warn("Failed to read response body for decompression", e);
						throw new Error("Failed to load resources (could not read body)");
					}
					const originalBytes = new Uint8Array(buffer); // original
					const workingBytes = originalBytes.slice(); // clone for decompression attempt
					// Detect gzip via magic bytes (1f 8b) OR header; otherwise try deflate.
					const isGzipMagic = workingBytes.length >= 2 && workingBytes[0] === 0x1f && workingBytes[1] === 0x8b;
					const preferredAlgo = (/gzip/.test(encoding) || isGzipMagic) ? "gzip" : "deflate";

					try {
						if (typeof DecompressionStream !== "undefined") {
							console.log("Decompressing resources using DecompressionStream", preferredAlgo, workingBytes.byteLength);
							const inputStream = new ReadableStream({
								start(controller) {
									controller.enqueue(workingBytes);
									controller.close();
								}
							});
							const stream = inputStream.pipeThrough(new DecompressionStream(preferredAlgo));
							const decompressed = new Response(stream);
							return await decompressed.json();
						}
					} catch (decompressErr) {
						console.warn("Decompression via DecompressionStream failed, falling back", decompressErr);
						// If we guessed deflate but magic suggests gzip, try a second attempt with gzip.
						if (preferredAlgo !== "gzip" && typeof DecompressionStream !== "undefined" && (originalBytes[0] === 0x1f && originalBytes[1] === 0x8b)) {
							try {
								console.log("Retrying decompression assuming gzip after failure");
								const retryStream = new ReadableStream({
									start(controller) {
										controller.enqueue(originalBytes); // use original bytes
										controller.close();
									}
								});
								const gzStream = retryStream.pipeThrough(new DecompressionStream("gzip"));
								const retryResp = new Response(gzStream);
								return await retryResp.json();
							} catch (retryErr) {
								console.warn("Second decompression attempt (gzip) failed", retryErr);
							}
						}
					}

					// Fallback: treat bytes as already-decompressed UTF-8 JSON.
					try {
						const text = new TextDecoder("utf-8").decode(originalBytes);
						return JSON.parse(text);
					} catch (e) {
						throw new Error("Failed to load resources (compressed JSON could not be decompressed)");
					}
				}

				// Not compressed; parse as JSON directly
				return await response.json().catch((err) => {
					throw new Error(err || "Failed to load resources (not JSON)");
				});
			})
			.catch(err => {
				console.error("Failed to load resources", err);

				throw new Error("Failed to load resources");
			});

		if (r) {
			yield put(ResourcesLoadSuccessAction(r));
		} else {
			yield put(ResourcesLoadFailedAction("Failed to load resources"));
		}
	} catch (e) {
		yield put(ResourcesLoadFailedAction(e));
	}
}

// Saga
export function * loadResourcesSaga () {
	yield takeLatest(ResourcesLoadAction.getType(), loadResourcesWorker);
}

// Export

export const loadSagas = [
	loadResourcesSaga()
];

export default loadSagas;
